// PWA Manager Component - Handles PWA install prompts and updates

import React, { useState, useEffect } from 'react'
import { PWAInstallPrompt } from './PWAInstallPrompt'
import { PWAUpdateNotification } from './PWAUpdateNotification'
import { usePWAInstall, useServiceWorker, useOffline } from '../../hooks/usePWA'

interface PWAManagerProps {
  showInstallPromptAfterDelay?: number // milliseconds
  installPromptVariant?: 'banner' | 'modal' | 'floating'
}

export const PWAManager: React.FC<PWAManagerProps> = ({
  showInstallPromptAfterDelay = 30000, // 30 seconds
  installPromptVariant = 'floating'
}) => {
  const { canInstall, isStandalone } = usePWAInstall()
  const { hasUpdate } = useServiceWorker()
  const { isOnline } = useOffline()

  const [showInstallPrompt, setShowInstallPrompt] = useState(false)
  const [installPromptDismissed, setInstallPromptDismissed] = useState(false)
  const [updateNotificationDismissed, setUpdateNotificationDismissed] = useState(false)

  // Handle install prompt timing
  useEffect(() => {
    if (!canInstall || isStandalone || installPromptDismissed) {
      return
    }

    // Check if user has already been prompted recently
    const lastPrompted = localStorage.getItem('pwa-install-prompted')
    const lastPromptedTime = lastPrompted ? parseInt(lastPrompted) : 0
    const oneDayAgo = Date.now() - (24 * 60 * 60 * 1000)

    if (lastPromptedTime > oneDayAgo) {
      return // Don't prompt again within 24 hours
    }

    // Show prompt after delay
    const timer = setTimeout(() => {
      setShowInstallPrompt(true)
    }, showInstallPromptAfterDelay)

    return () => clearTimeout(timer)
  }, [canInstall, isStandalone, installPromptDismissed, showInstallPromptAfterDelay])

  const handleInstallPromptDismiss = () => {
    setShowInstallPrompt(false)
    setInstallPromptDismissed(true)
    
    // Remember that user was prompted
    localStorage.setItem('pwa-install-prompted', Date.now().toString())
  }

  const handleUpdateNotificationDismiss = () => {
    setUpdateNotificationDismissed(true)
  }

  // Don't show anything if app is already standalone (installed)
  if (isStandalone) {
    return (
      <>
        {hasUpdate && !updateNotificationDismissed && (
          <PWAUpdateNotification onDismiss={handleUpdateNotificationDismiss} />
        )}
      </>
    )
  }

  return (
    <>
      {/* Install prompt */}
      <PWAInstallPrompt
        showPrompt={showInstallPrompt}
        onDismiss={handleInstallPromptDismiss}
        variant={installPromptVariant}
      />

      {/* Update notification */}
      {hasUpdate && !updateNotificationDismissed && (
        <PWAUpdateNotification onDismiss={handleUpdateNotificationDismiss} />
      )}

      {/* Offline indicator */}
      {!isOnline && <OfflineIndicator />}
    </>
  )
}

// Simple offline indicator component
const OfflineIndicator: React.FC = () => {
  return (
    <div style={{
      position: 'fixed',
      top: '10px',
      left: '50%',
      transform: 'translateX(-50%)',
      background: '#ef4444',
      color: 'white',
      padding: '8px 16px',
      borderRadius: '20px',
      fontSize: '14px',
      fontWeight: '500',
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)'
    }}>
      <span>📡</span>
      You're offline
    </div>
  )
}